/**
 * Password Validation
 * การตรวจสอบรหัสผ่าน
 *
 * Validates password meets security requirements.
 * ตรวจสอบว่ารหัสผ่านเป็นไปตามข้อกำหนดด้านความปลอดภัย
 */

export interface PasswordRule {
  id: string
  labelEn: string
  labelTh: string
  test: (password: string) => boolean
}

export const PASSWORD_RULES: PasswordRule[] = [
  {
    id: 'minLength',
    labelEn: 'At least 8 characters',
    labelTh: 'อย่างน้อย 8 ตัวอักษร',
    test: (p) => p.length >= 8,
  },
  {
    id: 'uppercase',
    labelEn: 'At least 1 uppercase letter',
    labelTh: 'ตัวพิมพ์ใหญ่อย่างน้อย 1 ตัว',
    test: (p) => /[A-Z]/.test(p),
  },
  {
    id: 'lowercase',
    labelEn: 'At least 1 lowercase letter',
    labelTh: 'ตัวพิมพ์เล็กอย่างน้อย 1 ตัว',
    test: (p) => /[a-z]/.test(p),
  },
  {
    id: 'number',
    labelEn: 'At least 1 number',
    labelTh: 'ตัวเลขอย่างน้อย 1 ตัว',
    test: (p) => /[0-9]/.test(p),
  },
  {
    id: 'special',
    labelEn: 'At least 1 special character',
    labelTh: 'อักขระพิเศษอย่างน้อย 1 ตัว',
    test: (p) => /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(p),
  },
]

/**
 * Validate a password against all rules.
 * ตรวจสอบรหัสผ่านตามกฎทั้งหมด
 *
 * @returns array of rule IDs that failed
 */
export const validatePassword = (password: string): string[] => {
  return PASSWORD_RULES.filter((rule) => !rule.test(password)).map((rule) => rule.id)
}

/**
 * Check if password meets all requirements.
 * ตรวจสอบว่ารหัสผ่านเป็นไปตามข้อกำหนดทั้งหมด
 */
export const isPasswordValid = (password: string): boolean => {
  return PASSWORD_RULES.every((rule) => rule.test(password))
}

/**
 * Get password strength (0-100).
 * ดึงความแข็งแกร่งของรหัสผ่าน (0-100)
 */
export const getPasswordStrength = (password: string): number => {
  if (!password) return 0
  const passed = PASSWORD_RULES.filter((rule) => rule.test(password)).length
  return Math.round((passed / PASSWORD_RULES.length) * 100)
}
